<template>
  <div class="tickets-insurance-container">
    <h2 class="component-title">{{ $t('protectTicketPurchase') }}</h2>
    <div class="insurance-options">
      <div
        :class="[
          'insurance-card',
          modelValue.selected && 'insurance-card-selected',
          shouldShowSelectionError && 'insurance-card-error',
        ]"
      >
        <div
          :class="[
            'insurance-card-header',
            (isInsuranceExpanded || modelValue.selected) && 'insurance-card-header-bordered',
          ]"
          @click="handleInsuranceHeaderClick"
        >
          <div class="option-row">
            <span
              :class="[
                'material-icons',
                'option-icon',
                !modelValue.selected && 'option-icon-muted',
              ]"
            >
              {{ modelValue.selected ? 'check_circle' : 'radio_button_unchecked' }}
            </span>
            <h3
              :class="[
                'option-title',
                modelValue.selected ? 'option-title-selected' : 'option-title-muted',
              ]"
            >
              {{ $t('addTicketInsuranceRecommended') }}
            </h3>
          </div>
          <div class="price-row">
            <p
              v-if="ticketInsuranceAmount"
              :class="[
                'option-price',
                modelValue.selected ? 'option-price-selected' : 'option-price-muted',
              ]"
            >
              + {{ ticketInsuranceAmount }} €
            </p>
            <span class="material-icons chevron-icon">
              {{ isInsuranceExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
            </span>
          </div>
        </div>
        <v-expand-transition>
          <div v-if="isInsuranceExpanded" class="insurance-card-body">
            <div class="insurance-content">
              <p class="insurance-description">
                {{ $t('ticketInsuranceDescriptionPart1') }}
                <strong>{{ $t('ticketInsuranceDescriptionHighlighted') }}</strong>
                {{ $t('ticketInsuranceDescriptionPart2') }}
                <a
                  href="/insurance/ticket-insurance-terms-2026-10.pdf"
                  target="_blank"
                >{{ $t('insuranceTerms') }}</a>
                {{ $t('ticketInsuranceDescriptionPart3') }}
              </p>
              <div class="reasons-list">
                <div class="reason-item">
                  <span class="material-icons reason-icon">sick</span>
                  <div class="reason-text-group">
                    <p class="reason-main">{{ $t('insuranceReasonHealth') }}</p>
                    <p class="reason-sub">{{ $t('insuranceReasonHealthSub') }}</p>
                  </div>
                </div>
                <div class="reason-item">
                  <span class="material-icons reason-icon">personal_injury</span>
                  <p class="reason-main">{{ $t('insuranceReasonAccidents') }}</p>
                </div>
                <div class="reason-item">
                  <span class="material-icons reason-icon">directions_car</span>
                  <p class="reason-main">{{ $t('insuranceReasonTraffic') }}</p>
                </div>
                <div class="reason-item">
                  <span class="material-icons reason-icon">tram</span>
                  <p class="reason-main">{{ $t('insuranceReasonPublicTransport') }}</p>
                </div>
                <div class="reason-item">
                  <span class="material-icons reason-icon">local_fire_department</span>
                  <div class="reason-text-group">
                    <p class="reason-main">{{ $t('insuranceReasonEmergency') }}</p>
                    <p class="reason-sub">{{ $t('insuranceReasonEmergencySub') }}</p>
                  </div>
                </div>
                <div class="reason-item">
                  <span class="material-icons reason-icon">description</span>
                  <div class="reason-text-group">
                    <p class="reason-main">{{ $t('insuranceReasonOther') }}</p>
                    <p class="reason-sub">{{ $t('insuranceReasonOtherSub') }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-expand-transition>
        <v-slide-y-transition>
          <div
            v-if="modelValue.selected"
            :class="['terms-confirmation', !isInsuranceExpanded && 'terms-confirmation-collapsed']"
          >
            <span
              :class="[
                'material-icons',
                'checkbox-icon',
                shouldShowTermsError && 'checkbox-icon-error',
              ]"
              @click.stop="toggleTermsAcceptance"
            >
              {{ modelValue.isTermsAccepted ? 'check_box' : 'check_box_outline_blank' }}
            </span>
            <p class="terms-text">
              {{ $t('insuranceConfirmationPart1') }}
              <a
                href="/insurance/ticket-insurance-terms-2026-10.pdf"
                target="_blank"
              >{{ $t('insuranceTerms') }}</a>
              {{ $t('insuranceConfirmationPart2') }}
            </p>
          </div>
        </v-slide-y-transition>
        <v-fade-transition>
          <p v-if="modelValue.selected && shouldShowTermsError" class="insurance-error-text">
            {{ $t('insuranceTermsRequired') }}
          </p>
        </v-fade-transition>
      </div>
      <div
        :class="[
          'insurance-card',
          modelValue.selected === false && 'insurance-card-selected-no',
          shouldShowSelectionError && 'insurance-card-error',
        ]"
        @click="unselectOption"
      >
        <div class="insurance-card-header insurance-card-header-small">
          <div class="option-row">
            <span
              :class="[
                'material-icons',
                'option-icon',
                (modelValue.selected === null || modelValue.selected) && 'option-icon-muted',
              ]"
            >
              {{ modelValue.selected === false ? 'check_circle' : 'radio_button_unchecked' }}
            </span>
            <h3
              :class="[
                'option-title',
                modelValue.selected === false ? 'option-title-selected' : 'option-title-muted',
              ]"
            >
              {{ $t('noTicketInsurance') }}
            </h3>
          </div>
          <div class="price-row">
            <p
              :class="[
                'option-price',
                modelValue.selected === false ? 'option-price-selected' : 'option-price-muted',
              ]"
            >
              {{ $t('noSurcharge') }}
            </p>
          </div>
        </div>
      </div>
      <v-fade-transition>
        <p v-if="shouldShowSelectionError" class="insurance-error-text">
          {{ $t('insuranceSelectionRequired') }}
        </p>
      </v-fade-transition>
    </div>
  </div>
</template>

<script>
export default {
  name: 'tickets-insurance-container',
  props: {
    value: {
      default: () => ({
        selected: null,
        isTermsAccepted: false,
      }),
      type: Object,
    },
    validation: {
      type: Object,
      required: true,
    },
    ticketInsuranceAmount: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isInsuranceExpanded: true,
    }
  },
  computed: {
    modelValue() {
      return {
        selected: null,
        isTermsAccepted: false,
        ...(this.value || {}),
      }
    },
    insuranceValidation() {
      return this.validation && this.validation.form && this.validation.form.insurance
    },
    shouldShowSelectionError() {
      return !!(
        this.insuranceValidation &&
        this.insuranceValidation.selected &&
        this.insuranceValidation.selected.$error
      )
    },
    shouldShowTermsError() {
      return (
        !!this.modelValue.selected &&
        !!(
          this.insuranceValidation &&
          this.insuranceValidation.isTermsAccepted &&
          this.insuranceValidation.isTermsAccepted.$error
        )
      )
    },
  },
  methods: {
    touchInsuranceField(field) {
      if (this.insuranceValidation && this.insuranceValidation[field]) {
        this.insuranceValidation[field].$touch()
      }
    },
    emitUpdatedModel(updatedValues) {
      this.$emit('input', {
        ...this.modelValue,
        ...updatedValues,
      })
    },
    handleInsuranceHeaderClick() {
      if (this.modelValue.selected) {
        this.isInsuranceExpanded = !this.isInsuranceExpanded

        return
      }
      this.isInsuranceExpanded = true
      this.touchInsuranceField('selected')
      this.emitUpdatedModel({
        selected: true,
      })
    },
    toggleTermsAcceptance() {
      this.touchInsuranceField('isTermsAccepted')
      this.emitUpdatedModel({
        isTermsAccepted: !this.modelValue.isTermsAccepted,
      })
    },
    unselectOption() {
      this.touchInsuranceField('selected')
      this.emitUpdatedModel({
        selected: false,
        isTermsAccepted: false,
      })
    },
  },
}
</script>

<style scoped>
.tickets-insurance-container {
  border-bottom: 1px solid rgba(151, 151, 151, 0.5);
  padding: 2rem 1.5rem 2.5rem;
  margin: 0 -1.5rem 1.5rem -1.5rem;
}

.component-title {
  font-family: 'Raleway';
  font-weight: 600;
  font-size: 1.5rem;
  line-height: 1.2;
  letter-spacing: -0.045rem;
  color: #333333;
  margin: 0 0 2.5rem 0;
}

.insurance-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.insurance-card {
  width: 100%;
  border: 1px solid rgba(151, 151, 151, 0.5);
  border-radius: 4px;
  overflow: hidden;
  background: #ffffff;
}

.insurance-card-selected {
  border-color: #0277bd;
}

.insurance-card-selected-no {
  border-color: #0277bd;
}

.insurance-card-error {
  border-color: #bd0202 !important;
}

.insurance-card-header {
  padding: 1rem 0.875rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  cursor: pointer;
}

.insurance-card-header-bordered {
  border-bottom: 1px solid rgba(151, 151, 151, 0.5);
}

.insurance-card-header-small {
  border-bottom: none;
}

.option-row {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.option-icon {
  font-size: 24px;
  line-height: 24px;
  color: #0277bd;
}

.option-icon-muted {
  color: #979797;
}

.option-title {
  margin: 0;
  font-family: 'Raleway';
  font-size: 1.25rem;
  line-height: 1.2;
  letter-spacing: -0.0375rem;
  font-weight: 600;
}

.option-title-selected {
  color: #0277bd;
}

.option-title-muted {
  color: #979797;
}

.price-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
}

.option-price {
  margin: 0;
  font-family: 'Raleway';
  font-size: 1.25rem;
  line-height: 1.2;
  letter-spacing: -0.0375rem;
  font-weight: 600;
}

.option-price-selected {
  color: #0277bd;
}

.option-price-muted {
  color: #979797;
}

.chevron-icon {
  font-size: 24px;
  color: #333333;
}

.insurance-card-body {
  padding: 2rem 1.5rem;
  overflow: hidden;
}

.insurance-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.insurance-description {
  margin: 0;
  font-family: 'Source Sans Pro';
  font-size: 0.875rem;
  line-height: 1.2;
  color: #333333;
}

.insurance-description a {
  color: #0277bd;
  text-decoration: none;
}

.reasons-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.reason-item {
  display: flex;
  gap: 0.25rem;
  align-items: flex-start;
}

.reason-icon {
  font-size: 16px;
  color: #0277bd;
}

.reason-text-group {
  display: flex;
  flex-direction: column;
}

.reason-main {
  margin: 0;
  font-family: 'Source Sans Pro';
  font-size: 0.875rem;
  line-height: 1.2;
  font-weight: 700;
  color: #333333;
}

.reason-sub {
  margin: 0;
  font-family: 'Source Sans Pro';
  font-size: 0.875rem;
  line-height: 1.2;
  color: #666666;
}

.terms-confirmation {
  border-top: 1px solid rgba(151, 151, 151, 0.5);
  padding: 1rem 0.875rem;
  display: flex;
  gap: 1rem;
  align-items: center;
}

.terms-confirmation-collapsed {
  padding: 1rem 0.875rem;
  display: flex;
  gap: 1rem;
  align-items: center;
}

.checkbox-icon {
  font-size: 24px;
  color: #0277bd;
  cursor: pointer;
}

.checkbox-icon-error {
  color: #bd0202;
}

.terms-text {
  margin: 0;
  font-family: 'Raleway';
  font-size: 1rem;
  line-height: 1.2;
  font-weight: 500;
  letter-spacing: 0.015rem;
  color: #333333;
}

.terms-text a {
  color: #0277bd;
  text-decoration: none;
}

.insurance-error-text {
  margin: 0;
  font-family: 'Source Sans Pro';
  font-size: 0.875rem;
  line-height: 1.2;
  color: #bd0202;
  padding: 0 1.5rem 1rem 1.5rem;
}

::v-deep .expand-transition-enter-active,
::v-deep .expand-transition-leave-active {
  transition: height 0.36s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.24s ease;
}

::v-deep .expand-transition-enter,
::v-deep .expand-transition-leave-to {
  opacity: 0;
}

::v-deep .slide-y-transition-enter-active,
::v-deep .slide-y-transition-leave-active {
  transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.2s ease;
}

@media screen and (max-width: 455px) {
  .component-title {
    margin-bottom: 2rem;
  }
}

@media screen and (min-width: 992px) {
  .insurance-card-header {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  .option-row {
    align-items: center;
    flex: 1 1 auto;
  }

  .price-row {
    flex: 0 0 auto;
    align-self: center;
  }

  .insurance-card-body {
    padding: 2rem;
  }

  .insurance-content {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.5rem;
  }

  .insurance-description {
    flex: 1 1 0;
    max-width: 360px;
  }

  .reasons-list {
    flex: 0 0 342px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  .insurance-card-header-small {
    flex-direction: row;
    align-items: center;
  }

  .insurance-card-header-small .option-row {
    flex: 1 1 auto;
  }

  .insurance-card-header-small .price-row {
    align-self: center;
  }

  .insurance-card-header,
  .terms-confirmation,
  .terms-confirmation-collapsed {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
}
</style>
